import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Table, SimpleTextFilter, getVisibleColumns } from '../../../common/Table';
import Loading from '../../../common/Loading';
import KwPanel from '../../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../../hooks/useTableSettings';
import { DateOnly_AsString_NoHtml } from '../../../../util/formatter';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import { useTranslation } from 'react-i18next';
import {
  exportAnnouncementsData,
  getAnnouncements,
} from '../../../../api/announcementApi';

interface ListApiResponse<T> {
  data: T[];
  totalRowCount: number;
  filteredRowCount: number;
}

interface AnnouncementItem {
  id: string;
  message?: string;
  announcementDate?: string;
  expiryDate?: string;
}

export interface AnnouncementListProps {
  reload?: Date | undefined;
}

function AnnouncementList({ reload }: AnnouncementListProps) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const { t } = useTranslation();

  const [announcementDateDisplayValue, setAnnouncementDateDisplayValue] =
    useState<string | undefined>(undefined);
  useEffect(() => {
    if (!announcementDateDisplayValue) {
      let _from = params.get(`f_announcementDateFrom`) || null;
      let _to = params.get(`f_announcementDateTo`) || null;
      if (_from && _to) {
        let fromDate = Date.parse(_from);
        let toDate = Date.parse(_to);
        if (isNaN(fromDate) || isNaN(toDate)) {
          setAnnouncementDateDisplayValue('');
        } else {
          setAnnouncementDateDisplayValue(`${_from} - ${_to}`);
        }
      }
    }
  }, [params, announcementDateDisplayValue]);

  const allColumns = ['message', 'announcementDate', 'expiryDate'];
  const defaultVisibleColumns = allColumns;
  const [announcements, setAnnouncements] = useState<ListApiResponse<AnnouncementItem> | undefined>(undefined);
  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    filtersChange,
    isLoading,
  } = useTableSettings(
    'announcementlist-settings',
    getTableSettingsDefaults({
      defaultSort: 'announcementDate',
      defaultVisibleColumns,
    }),
    null,
    getAnnouncements,
    setAnnouncements,
    reload,
    t('Failed to load announcements')
  );

  // The base config of the columns, this will be filtered based on the visible (above)
  let columnsConfig = [
    {
      Key: 'message',
      Label: t('Message'),
      width: '40%',
      Render: (announcement: AnnouncementItem) => (
        <Link to={'/settings/announcement/' + announcement.id}>
          {announcement.message}
        </Link>
      ),
      Filter: {
        Render: (col: { Key: string; filterValue?: string }) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'announcementDate',
      Label: t('Announcement date'),
      Render: (item: AnnouncementItem) => DateOnly_AsString_NoHtml(item.announcementDate, undefined),
      Filter: {
        Render: (_col: { Key: string }) => (
          <DateRangePicker
            initialSettings={{
              showDropdowns: true,
              minYear: 2010,
              buttonClasses: 'btn btn-default',
              autoUpdateInput: false,
              locale: {
                format: 'DD-MMM-YYYY',
                applyLabel: t('Apply'),
                cancelLabel: t('Clear'),
              },
            }}
            onApply={(e, picker) => {
              let from = picker.startDate.format('DD-MMM-YYYY');
              let to = picker.endDate.format('DD-MMM-YYYY');
              filtersChange([
                { key: 'announcementDateFrom', value: from },
                { key: 'announcementDateTo', value: to },
              ]);
              setAnnouncementDateDisplayValue(`${from} - ${to}`);
            }}
            onCancel={(e, picker) => {
              picker.setStartDate(new Date());
              picker.setEndDate(new Date());
              filtersChange([
                { key: 'announcementDateFrom', value: '' },
                { key: 'announcementDateTo', value: '' },
              ]);
              setAnnouncementDateDisplayValue('');
            }}
          >
            <input
              type="text"
              className={`form-control ${
                announcementDateDisplayValue ? 'filtered' : ''
              }`}
              value={announcementDateDisplayValue}
              onChange={(e) => {
                setAnnouncementDateDisplayValue(e.target.value);
              }}
            />
          </DateRangePicker>
        ),
      },
    },
    {
      Key: 'expiryDate',
      Label: t('Expires'),
      Render: (item: AnnouncementItem) => DateOnly_AsString_NoHtml(item.expiryDate, undefined),
    },
  ];

  // filter
  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  // return an alaska table
  return announcements ? (
    <KwPanel className="mb-4">
      <Table
        columns={columns}
        allColumns={allColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        rows={announcements.data.map((x: AnnouncementItem) => {
          return { Key: x.id, ...x };
        })}
        totalRowCount={announcements.totalRowCount}
        filteredRowCount={announcements.filteredRowCount}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        filters={filters}
        isLoading={isLoading}
        filterChange={filterChange}
        exportData={(filters, scopes) =>
          exportAnnouncementsData({
            filters: (filters ?? {}) as Record<string, string>,
            scopes: (scopes ?? {}) as Record<string, string>,
          })
        }
      />
    </KwPanel>
  ) : (
    <Loading />
  );
}
export default AnnouncementList;
